'use client'

import { useTranslations } from 'next-intl'
import { useState, type FC, useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { VariantFormLabel } from '@/components/common/form/variant-form-label'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useToast } from '@/components/ui/use-toast'

import { useReplaceParams } from '@/hooks/use-replace-params'
import { useZodResolver } from '@/hooks/use-zod-resolver'
import { sleep } from '@/lib/utils'
import { ForgotPasswordSchema } from '@/schema/auth-schema'
import { authForgotPasswordService } from '@/services/auth-service'

const DEFAULT_COUNTDOWN_DURATION = 60 // 60 seconds

export const AuthForgotPasswordFormComponent: FC = () => {
  const tFormCommon = useTranslations('Auth.forms.common')
  const tFormCurrent = useTranslations('Auth.forms.forgot_password_form')
  const tFormFields = useTranslations('Auth.forms.forgot_password_form.fields')
  const resolver = useZodResolver({ schema: ForgotPasswordSchema, tFormFields })
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [isDisabled, setIsDisabled] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const countdownDuration = DEFAULT_COUNTDOWN_DURATION

  useEffect(() => {
    let timer: ReturnType<typeof setInterval>
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setIsDisabled(false)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    return () => {
      if (timer) clearInterval(timer)
    }
  }, [countdown])

  const { replaceParams } = useReplaceParams()
  const form = useForm<typeof ForgotPasswordSchema._type>({
    resolver,
    defaultValues: {
      email: '',
    },
  })

  const onSubmit = useCallback(
    async (values: typeof ForgotPasswordSchema._type) => {
      try {
        setIsLoading(true)
        const { email } = values
        const { ok, error, message } = await authForgotPasswordService({ email })

        if (ok) {
          setIsDisabled(true)
          setCountdown(countdownDuration)
          toast({
            variant: 'success',
            description: tFormCurrent('messages.submit_success'),
          })
        }

        if (error) {
          console.error(error, message)
          toast({
            variant: 'error',
            description: tFormCommon('messages.default_error'),
          })
          throw new Error(error)
        }
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
      }
    },
    [setIsLoading, authForgotPasswordService, toast, tFormCommon, sleep, replaceParams],
  )

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <VariantFormLabel>{tFormFields('email')}</VariantFormLabel>
                <FormControl>
                  <Input {...field} placeholder={tFormCurrent('placeholders.email')} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" className="w-full" disabled={isLoading || isDisabled}>
            {tFormCurrent('actions.form_submit')}
          </Button>
          {isDisabled && countdown > 0 && (
            <p className="mt-4 text-xs text-muted-foreground">
              {tFormCurrent('messages.countdown_message', { time: countdown })}
            </p>
          )}
        </form>
      </Form>
    </>
  )
}
